
import React, { useEffect, useState } from 'react';
import { AppProviders } from "./components/providers/AppProviders";
import AppInitializer from "./components/app/AppInitializer";
import RouteManager from "./routes/RouteManager";
import NavigationHandler from './components/navigation/NavigationHandler';
import AppRoutes from './routes/AppRoutes';
import AppLayout from './components/layout/AppLayout';
import { RouteProvider } from './contexts/RouteContext';
import ServiceWorkerUpdater from './components/pwa/ServiceWorkerUpdater';
import SplashScreen from './components/common/SplashScreen';
import { StorageStatusIndicator } from './components/storage/StorageStatusIndicator';

const App = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [appReady, setAppReady] = useState(false);

  // Fast initialization - no delays
  useEffect(() => {
    console.log('NALC PWA initialized - localStorage only mode');
    
    // Quick setup for localStorage-only mode
    const initializeApp = async () => {
      try {
        // Initialize storage immediately
        const { pwaStorageManager } = await import('./services/storage/pwaStorageManager');
        await pwaStorageManager.initialize();
        
        // Register PWA install prompt
        const handleBeforeInstallPrompt = (e: Event) => {
          e.preventDefault();
          console.log('PWA installation prompt available');
          (window as any).deferredPrompt = e;
        };
        
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        
        setAppReady(true);
        
        return () => {
          window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
      } catch (error) {
        console.error('App initialization error:', error);
        setAppReady(true); // Continue anyway
      }
    };

    initializeApp();
  }, []);

  const handleSplashFinish = () => {
    console.log('Splash screen finished, showing main app');
    setShowSplash(false);
  };

  // Don't render anything until app is ready
  if (!appReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading NALC App...</p>
        </div>
      </div>
    );
  }

  console.log('Rendering App component');
  
  return (
    <AppProviders>
      {showSplash ? (
        <SplashScreen onFinish={handleSplashFinish} duration={7000} />
      ) : (
        <AppInitializer>
          <RouteProvider>
            <NavigationHandler>
              <AppLayout>
                <RouteManager>
                  <AppRoutes />
                </RouteManager>
              </AppLayout>
            </NavigationHandler>
          </RouteProvider>
        </AppInitializer>
      )}
      <ServiceWorkerUpdater />
      <StorageStatusIndicator />
    </AppProviders>
  );
};

export default App;
